import React, { useState, useMemo, useCallback } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronRight, ChevronDown, X, Plus, Layers, Maximize2, Minimize2, GripVertical } from 'lucide-react';

const STATUS_LABELS = { in_preparazione: 'In Preparazione', partito: 'Partito', congelato: 'Congelato', terminato: 'Terminato' };

const CORE_DIMS = [
  { key: 'pm', label: 'Project Manager' },
  { key: 'client', label: 'Cliente' },
  { key: 'project', label: 'Progetto' },
  { key: 'venditore', label: 'Operativo' },
  { key: 'product', label: 'Prodotto' },
  { key: 'project_status', label: 'Stato Progetto' },
  { key: 'activity_type', label: 'Tipo Attivita' },
  { key: 'contract', label: 'Contratto' },
];

const DEFAULT_ACTIVE = [
  { key: 'pm', label: 'Project Manager' },
  { key: 'client', label: 'Cliente' },
  { key: 'project', label: 'Progetto' },
  { key: 'venditore', label: 'Operativo' },
];

function getDimValues(record, key) {
  switch (key) {
    case 'pm':
      return (record.pm_ids || []).length > 0
        ? record.pm_ids.map((id, i) => ({ id, name: (record.pm_names || [])[i] || 'N/A' }))
        : [{ id: '__no_pm__', name: 'Nessun PM' }];
    case 'client': return [{ id: record.client_id || '__none__', name: record.client_name || 'N/A' }];
    case 'project': return [{ id: record.project_id || '__none__', name: record.project_name || 'N/A' }];
    case 'venditore': return [{ id: record.venditore_id || '__none__', name: record.venditore_name || 'N/A' }];
    case 'product': return [{ id: record.product_id || '__none__', name: record.product_name || 'N/A' }];
    case 'project_status': return [{ id: record.project_status || '__none__', name: STATUS_LABELS[record.project_status] || record.project_status || 'N/A' }];
    case 'activity_type': return [{ id: record.activity_type || '__none__', name: record.activity_type || 'N/A' }];
    case 'contract': return [{ id: record.contract_id || '__none__', name: record.contract_name || 'N/A' }];
    default:
      if (key.startsWith('cf_project_')) {
        const v = (record.project_cf || {})[key.replace('cf_project_', '')] || 'N/A';
        return [{ id: v, name: String(v) }];
      }
      if (key.startsWith('cf_client_')) {
        const v = (record.client_cf || {})[key.replace('cf_client_', '')] || 'N/A';
        return [{ id: v, name: String(v) }];
      }
      return [{ id: 'N/A', name: 'N/A' }];
  }
}

function agg(records) {
  const pids = new Set(), apids = new Set(), vids = new Set();
  let amt = 0, comm = 0;
  for (const r of records) {
    amt += r.amount || 0;
    comm += r.commission || 0;
    if (r.project_id) pids.add(r.project_id);
    if (r.is_active_project && r.project_id) apids.add(r.project_id);
    if (r.venditore_id) vids.add(r.venditore_id);
  }
  const n = apids.size;
  const nOps = vids.size;
  return {
    vendite: amt, success_fee: comm, n_progetti_attivi: n, n_operativi: nOps,
    media_vendite: n > 0 ? amt / n : 0, media_sf: n > 0 ? comm / n : 0,
    media_sf_op: nOps > 0 ? comm / nOps : 0,
    sf_pct: amt > 0 ? (comm / amt) * 100 : 0,
  };
}

function buildTree(records, dims, depth = 0) {
  if (!records.length || depth >= dims.length) return null;
  const groups = {};
  for (const r of records) {
    for (const { id, name } of getDimValues(r, dims[depth].key)) {
      const k = id || '__none__';
      if (!groups[k]) groups[k] = { id: k, name: name || 'N/A', recs: [] };
      groups[k].recs.push(r);
    }
  }
  return Object.values(groups)
    .map(g => ({ id: g.id, name: g.name, ...agg(g.recs), children: buildTree(g.recs, dims, depth + 1) }))
    .sort((a, b) => (b.vendite + b.success_fee) - (a.vendite + a.success_fee));
}

const fmt = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

function PivotDelta({ current, previous }) {
  if (previous === null || previous === undefined || previous === 0) return null;
  const delta = ((current - previous) / Math.abs(previous)) * 100;
  if (!isFinite(delta)) return null;
  const color = delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-gray-400';
  return <span className={`text-[10px] font-medium ${color} ml-1`}>{delta > 0 ? '+' : ''}{delta.toFixed(1)}%</span>;
}

const DEPTH_BG = ['', 'bg-slate-50/70', 'bg-slate-100/50', 'bg-slate-100/70', 'bg-slate-200/40'];
const DEPTH_BORDER = ['border-gray-200', 'border-gray-150', 'border-gray-100', 'border-gray-50', 'border-gray-50'];

function SortableDimChip({ dim, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dim.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className={`flex items-center bg-white border rounded-lg pl-1 pr-0.5 py-0.5 shadow-sm select-none ${isDragging ? 'border-blue-400 shadow-md ring-2 ring-blue-100' : 'border-gray-200'}`} data-testid={`dim-chip-${dim.key}`}>
      <button {...attributes} {...listeners} className="p-0.5 cursor-grab active:cursor-grabbing touch-none" data-testid={`dim-grip-${dim.key}`}>
        <GripVertical size={13} className="text-gray-300" />
      </button>
      <span className="text-[10px] font-bold text-white bg-gray-800 rounded w-4 h-4 flex items-center justify-center shrink-0">{index + 1}</span>
      <span className="text-xs font-medium text-gray-700 mx-1.5 whitespace-nowrap">{dim.label}</span>
      <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="p-0.5 hover:bg-red-50 rounded transition-colors" data-testid={`dim-remove-${dim.key}`}>
        <X size={12} className="text-gray-300 hover:text-red-500" />
      </button>
    </div>
  );
}

export default function PivotTab({ pivotData, prevPivotData, loading }) {
  const records = pivotData?.records || [];
  const prevRecords = prevPivotData?.records || [];
  const cfDefs = pivotData?.custom_field_defs || [];

  const [activeDims, setActiveDims] = useState(DEFAULT_ACTIVE);
  const [expanded, setExpanded] = useState(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [pivotSort, setPivotSort] = useState({ key: null, dir: 'desc' });

  const togglePivotSort = useCallback((key) => {
    setPivotSort(prev => prev.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setActiveDims(prev => {
        const oldIdx = prev.findIndex(d => d.key === active.id);
        const newIdx = prev.findIndex(d => d.key === over.id);
        return arrayMove(prev, oldIdx, newIdx);
      });
      setExpanded(new Set());
    }
  }, []);

  const allDims = useMemo(() => {
    const dims = [...CORE_DIMS];
    for (const cf of cfDefs) {
      const prefix = cf.entity_type === 'client' ? 'cf_client_' : 'cf_project_';
      dims.push({ key: `${prefix}${cf.id}`, label: cf.name });
    }
    return dims;
  }, [cfDefs]);

  const availDims = useMemo(() => {
    const active = new Set(activeDims.map(d => d.key));
    return allDims.filter(d => !active.has(d.key));
  }, [allDims, activeDims]);

  const tree = useMemo(() => {
    const raw = buildTree(records, activeDims) || [];
    if (!pivotSort.key) return raw;
    const sortNodes = (nodes) => {
      if (!nodes) return null;
      const sorted = [...nodes].sort((a, b) => pivotSort.dir === 'asc' ? (a[pivotSort.key] > b[pivotSort.key] ? 1 : -1) : (a[pivotSort.key] < b[pivotSort.key] ? 1 : -1));
      return sorted.map(n => ({ ...n, children: sortNodes(n.children) }));
    };
    return sortNodes(raw);
  }, [records, activeDims, pivotSort]);
  const totals = useMemo(() => agg(records), [records]);

  // Build previous period tree map for delta lookups
  const prevMap = useMemo(() => {
    if (!prevRecords.length) return {};
    const prevTree = buildTree(prevRecords, activeDims) || [];
    const map = {};
    const walk = (nodes, prefix) => {
      for (const n of nodes) {
        const p = `${prefix}/${n.id}`;
        map[p] = n;
        if (n.children?.length) walk(n.children, p);
      }
    };
    walk(prevTree, '');
    return map;
  }, [prevRecords, activeDims]);
  const prevTotals = useMemo(() => prevRecords.length ? agg(prevRecords) : null, [prevRecords]);

  const removeDim = useCallback((i) => {
    setActiveDims(prev => prev.filter((_, idx) => idx !== i));
    setExpanded(new Set());
  }, []);

  const addDim = useCallback((dim) => {
    setActiveDims(prev => [...prev, dim]);
    setShowAdd(false);
  }, []);

  const toggle = useCallback((path) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const paths = new Set();
    const walk = (nodes, prefix) => {
      if (!nodes) return;
      for (const n of nodes) {
        const p = `${prefix}/${n.id}`;
        if (n.children?.length) { paths.add(p); walk(n.children, p); }
      }
    };
    walk(tree, '');
    setExpanded(paths);
  }, [tree]);

  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  const SortHead = ({ label, sortKey, className = "text-right" }) => {
    const isActive = pivotSort.key === sortKey;
    return (
      <TableHead
        className={`${className} font-semibold text-gray-600 text-xs cursor-pointer hover:text-gray-900 select-none transition-colors`}
        onClick={() => togglePivotSort(sortKey)}
        data-testid={`pivot-sort-${sortKey}`}
      >
        <span className="inline-flex items-center gap-0.5">
          {label}
          {isActive && <span className="text-[9px]">{pivotSort.dir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
        </span>
      </TableHead>
    );
  };

  const renderRow = (node, parentPath, depth) => {
    const path = `${parentPath}/${node.id}`;
    const isExp = expanded.has(path);
    const hasCh = node.children?.length > 0;
    const prev = prevMap[path];
    return (
      <React.Fragment key={path}>
        <TableRow
          className={`${DEPTH_BG[depth] || ''} hover:bg-blue-50/50 transition-colors ${DEPTH_BORDER[depth] || ''} border-t`}
          onClick={() => hasCh && toggle(path)}
          style={{ cursor: hasCh ? 'pointer' : 'default' }}
          data-testid={`pivot-row-d${depth}`}
        >
          <TableCell className="py-2">
            <div className="flex items-center" style={{ paddingLeft: `${depth * 24}px` }}>
              {hasCh ? (
                <span className="w-5 h-5 flex items-center justify-center text-gray-400 shrink-0">
                  {isExp ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                </span>
              ) : <span className="w-5 shrink-0" />}
              <span className={`ml-1 text-sm ${depth === 0 ? 'font-semibold text-gray-900' : depth === 1 ? 'font-medium text-gray-800' : 'text-gray-700'}`}>
                {node.name}
              </span>
            </div>
          </TableCell>
          <TableCell className="text-right tabular-nums text-sm py-2">
            {fmt(node.vendite)}<PivotDelta current={node.vendite} previous={prev?.vendite} />
          </TableCell>
          <TableCell className="text-right tabular-nums text-sm py-2 font-medium text-emerald-700">
            {fmt(node.success_fee)}<PivotDelta current={node.success_fee} previous={prev?.success_fee} />
          </TableCell>
          <TableCell className="text-right tabular-nums text-sm py-2">{node.n_progetti_attivi}</TableCell>
          <TableCell className="text-right tabular-nums text-sm py-2 text-gray-500">{fmt(node.media_vendite)}</TableCell>
          <TableCell className="text-right tabular-nums text-sm py-2 text-gray-500">{fmt(node.media_sf)}</TableCell>
          <TableCell className="text-right tabular-nums text-sm py-2 text-gray-500">{fmt(node.media_sf_op)}</TableCell>
          <TableCell className="text-right py-2">
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${node.sf_pct >= 15 ? 'text-emerald-600 border-emerald-200 bg-emerald-50' : node.sf_pct >= 8 ? 'text-amber-600 border-amber-200 bg-amber-50' : node.sf_pct > 0 ? 'text-red-600 border-red-200 bg-red-50' : 'text-gray-400 border-gray-200'}`}>
              {node.sf_pct.toFixed(1)}%
            </Badge>
          </TableCell>
        </TableRow>
        {isExp && hasCh && node.children.map(ch => renderRow(ch, path, depth + 1))}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800" />
        <span className="ml-3 text-gray-500">Caricamento dati pivot...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pivot-tab">
      {/* Dimension Selector */}
      <Card className="border border-gray-100 shadow-sm">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 mr-1 shrink-0">
              <Layers size={15} className="text-gray-400" />
              <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Livelli</span>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={activeDims.map(d => d.key)} strategy={horizontalListSortingStrategy}>
                {activeDims.map((dim, i) => (
                  <SortableDimChip key={dim.key} dim={dim} index={i} onRemove={() => removeDim(i)} />
                ))}
              </SortableContext>
            </DndContext>
            {availDims.length > 0 && (
              <div className="relative">
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-dashed" onClick={() => setShowAdd(!showAdd)} data-testid="add-dimension-btn">
                  <Plus size={13} /> Aggiungi livello
                </Button>
                {showAdd && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowAdd(false)} />
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[220px] max-h-[300px] overflow-y-auto py-1" data-testid="add-dimension-menu">
                      <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Dimensioni disponibili</div>
                      {availDims.map(dim => (
                        <button key={dim.key} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors" onClick={() => addDim(dim)} data-testid={`add-dim-${dim.key}`}>
                          {dim.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="ml-auto flex gap-1 shrink-0">
              <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-gray-500" onClick={expandAll} data-testid="expand-all-btn">
                <Maximize2 size={13} /> Espandi
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-gray-500" onClick={collapseAll} data-testid="collapse-all-btn">
                <Minimize2 size={13} /> Comprimi
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pivot Table */}
      <Card className="border border-gray-100 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Layers size={16} className="text-blue-500" />
            <CardTitle className="text-base font-semibold text-gray-800">Tabella Pivot</CardTitle>
          </div>
          <CardDescription className="text-xs">
            {activeDims.map(d => d.label).join(' \u2192 ')}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {records.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-200 bg-gray-50/50">
                    <TableHead className="min-w-[280px] font-semibold text-gray-600 text-xs">Dimensione</TableHead>
                    <SortHead label="Vendite" sortKey="vendite" />
                    <SortHead label="Success Fee" sortKey="success_fee" />
                    <SortHead label="Proj. Attivi" sortKey="n_progetti_attivi" />
                    <SortHead label="Media Vend." sortKey="media_vendite" />
                    <SortHead label="Media SF" sortKey="media_sf" />
                    <SortHead label="Media SF/Op." sortKey="media_sf_op" />
                    <SortHead label="SF%" sortKey="sf_pct" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-gray-100/80 border-b-2 border-gray-300" data-testid="pivot-totals-row">
                    <TableCell className="py-2.5 text-sm font-bold text-gray-900">TOTALE GENERALE</TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-bold">
                      {fmt(totals.vendite)}<PivotDelta current={totals.vendite} previous={prevTotals?.vendite} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-bold text-emerald-700">
                      {fmt(totals.success_fee)}<PivotDelta current={totals.success_fee} previous={prevTotals?.success_fee} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-bold">{totals.n_progetti_attivi}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-bold">{fmt(totals.media_vendite)}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-bold">{fmt(totals.media_sf)}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-bold">{fmt(totals.media_sf_op)}</TableCell>
                    <TableCell className="text-right text-sm font-bold">{totals.sf_pct.toFixed(1)}%</TableCell>
                  </TableRow>
                  {tree.map(node => renderRow(node, '', 0))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
              Nessun dato disponibile per i filtri selezionati
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
